<template>
  <div class="box">
    <div class="month-selector">
      <div v-for="date in dateMenu" v-bind:key="date.month">
        <div class="month" v-on:click="changeSelectedMonth(date)" @mouseover="hoverMonth = date.monthName" @mouseout="hoverMonth = null" v-bind:class="[ {selected: (date.monthName == selectedMonth.monthName)}, {unselected: (date.monthName != selectedMonth.monthName)}, {hover: (date.monthName == hoverMonth && date.monthName != selectedMonth.monthName)} ]">
          {{ date.monthName }}
        </div>
      </div>
    </div>
    <vue-loading v-if="loading" :active.sync="loading" :is-full-page="true" loader="dots" color="#70A1F0"></vue-loading>
    <div v-else class="zendesk-page">
      <div class="zendesk-summary">
        <div class="zendesk-download">
          <button type="button" class="btn btn-sm btn-primary" v-on:click="downloadCSV('summary')">Download Report</button>
        </div>
        <vue-good-table
          :columns="summaryCols"
          :rows="summary"
          :search-options="{
            enabled: true,
            skipDiacritics: true,
            placeholder: 'filter'
          }"
          @on-row-dblclick="changeContract"
          styleClass="vgt-table condensed">
        </vue-good-table>
      </div>
      <div class="zendesk-tickets">
        <div class="zendesk-download">
          <button type="button" class="btn btn-sm btn-primary" v-on:click="downloadCSV('tickets')">Download Report</button>
        </div>
        <vue-good-table
          :columns="ticketsCols"
          :rows="tickets"
          :search-options="{
            enabled: true,
            skipDiacritics: true,
            placeholder: 'filter'
          }"
          @on-row-dblclick="openTicket"
          styleClass="vgt-table condensed">
        </vue-good-table>
      </div>
    </div>
  </div>
</template>

<script>
import HTTP from '@/common/http'
import { mapGetters } from 'vuex'
import VueLoading from 'vue-loading-overlay'

export default {
  components: {
    VueLoading
  },
  props: [
    'id'
  ],
  computed: {
    ...mapGetters([
      'activeType'
    ]),
    dateMenu () {
      // Current month at the end of the array, previous 11 month before it in chronological order
      let lastYearMonths = this.monthNumbers.slice(this.monthNumbers.indexOf(this.currentMonth()) + 1)
      lastYearMonths = lastYearMonths.map(x => ({'year': this.currentYear() - 1, 'monthNumber': x, 'monthName': this.monthNames[x]}))
      let thisYearMonths = this.monthNumbers.slice(0, this.monthNumbers.indexOf(this.currentMonth()) + 1)
      thisYearMonths = thisYearMonths.map(x => ({'year': this.currentYear(), 'monthNumber': x, 'monthName': this.monthNames[x]}))
      let orderedMonths = lastYearMonths.concat(thisYearMonths)
      return orderedMonths
    },
    selectedDate () {
      // Convert to YYYY-MM format for API
      return `${this.selectedMonth.year}-${('0' + (this.selectedMonth.monthNumber + 1)).slice(-2)}`
    }
  },
  created: function () {
    this.selectedMonth = this.dateMenu.slice(-1)[0]
  },
  methods: {
    getData: function () {
      this.loading = true
      this.summary = []
      this.tickets = []
      HTTP.get(`zendesk/summary/${this.activeType}/${this.id}/${this.selectedDate}`)
        .then((response) => {
          this.summary = response.data.summary
          this.tickets = response.data.tickets
        })
        .catch((error) => {
          this.flashMessage.error({
            message: `Failed to get Zendesk Summary: ${error.message}`
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    openTicket (params) {
      window.open(params.row.link, '_blank').focus()
    },
    changeContract (params) {
      this.$store.dispatch('SET_ACTIVE', params.row.contract)
    },
    changeSelectedMonth (month) {
      this.selectedMonth = month
    },
    currentMonth () {
      const d = new Date()
      return d.getMonth()
    },
    currentYear () {
      const d = new Date()
      return d.getFullYear()
    },
    downloadCSV: function (reportType) {
      this.flashMessage.info({
        message: `Generating Zendesk ${reportType} CSV report`,
        time: 3000
      })
      HTTP.get(`/zendesk/${this.activeType}/${this.id}/${reportType}/csv/${this.selectedDate}`)
        .then((response) => {
          window.location = response.data.signedUrl
          this.flashMessage.success({
            message: `Zendesk ${reportType} CSV report generation complete`
          })
        })
        .catch((error) => {
          this.flashMessage.error({
            message: `Failed to generate Zendesk ${reportType} CSV report : ${error.message}`
          })
        })
    }
  },
  data () {
    return {
      loading: false,
      monthNumbers: [...Array(12).keys()],
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      selectedMonth: null,
      hoverMonth: null,
      summary: [],
      tickets: [],
      summaryCols: [
        {
          label: 'Contract',
          field: 'contract'
        },
        {
          label: 'Total',
          field: 'total',
          type: 'number',
          firstSortType: 'desc'
        },
        {
          label: 'New',
          field: 'new',
          type: 'number',
          firstSortType: 'desc'
        },
        {
          label: 'Open',
          field: 'open',
          type: 'number',
          firstSortType: 'desc'
        },
        {
          label: 'Pending',
          field: 'pending',
          type: 'number',
          firstSortType: 'desc'
        },
        {
          label: 'Hold',
          field: 'hold',
          type: 'number',
          firstSortType: 'desc'
        },
        {
          label: 'Solved',
          field: 'solved',
          type: 'number',
          firstSortType: 'desc'
        },
        {
          label: 'Closed',
          field: 'closed',
          type: 'number',
          firstSortType: 'desc'
        }
      ],
      ticketsCols: [
        {
          label: 'Contract',
          field: 'contract'
        },
        {
          label: 'ID',
          field: 'ticket_id'
        },
        {
          label: 'Status',
          field: 'status'
        },
        {
          label: 'Created',
          field: 'date_created',
          width: '100px'
        },
        {
          label: 'Updated',
          field: 'date_last_updated',
          width: '100px'
        },
        {
          label: 'Type',
          field: 'problem_type'
        },
        {
          label: 'Title',
          field: 'title'
        }
      ]
    }
  },
  watch: {
    id: function () {
      this.getData()
    },
    selectedMonth: function () {
      this.getData()
    }
  }
}
</script>

<style scoped>

.zendesk-page {
  display: flex;
}
.zendesk-summary {
  padding: 20px;
  padding-top: 10px;
  display: inline-block;
  position: relative;
}
.zendesk-tickets {
  padding: 20px;
  padding-top: 10px;
  padding-left: 0px;
  display: inline-block;
  position: relative;
  width: 100%;
}
.month-selector {
  padding: 20px;
  padding-bottom: 0px;
  display: flex;
  justify-content: space-between;
}
.month {
  padding: 1px;
  cursor: pointer;
}
.selected {
  font-weight: 800;
  font-size: larger;
  color: #606266;
  border-bottom: 4px solid #77a6f7;
}
.hover {
  font-weight: 200;
  font-size: larger;
  color: #606266;
  border-bottom: 4px solid #aaaaaa;
}
.unselected {
  font-weight: 200;
  font-size: larger;
  color: #606266;
}
@media screen and (min-width: 1400px) {
  /deep/ .vgt-left-align {
    font-size: 13px;
  }
  /deep/ .vgt-right-align {
    font-size: 13px;
  }
}
@media screen and (max-width: 1400px) {
  /deep/ .vgt-left-align {
    font-size: 11px;
  }
  /deep/ .vgt-right-align {
    font-size: 11px;
  }
}
/deep/ .vgt-global-search__input {
  padding-right: 160px;
}
.zendesk-download {
  top: 16px;
  right: 35px;
  position: absolute;
  cursor: pointer;
  z-index: 1;
}

</style>



// WEBPACK FOOTER //
// src/views/ZendeskSummary.vue